import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  DefaultDeviceController,
  DefaultMeetingSession,
  MeetingSessionConfiguration,
  MeetingSessionStatusCode,
} from "amazon-chime-sdk-js";

import { usePracticeDetails } from "features/practice/hooks/usePracticeDetails";

import styles from "./InlineVideoCall.module.css";
import { useVideoCallResilience } from "./useVideoCallResilience";
import {
  BufferedChimeLogger,
  buildReportMessage,
  CallStatsCollector,
  CHIME_MILESTONE_EVENTS,
  CHIME_PROBLEM_EVENTS,
  describeAudioPlayback,
  describeError,
  getMicrophonePermissionState,
  isDiagnosticsReportingEnabled,
  summariseChimeEventAttributes,
} from "./videoCallDiagnostics";
import {
  checkWebRTCSupport,
  createBlurDeviceResolver,
  getConnectionQualityDescription,
  getConnectionQualityDetail,
  isBackgroundBlurSupported,
  isUnsuitableCamera,
  NetworkQuality,
  pickCameraDevice,
  VIDEO_CALL_CONFIG,
} from "./videoCallUtils";

const InlineVideoCall = ({
  credentials,
  meetingId,
  onPopOut,
  onLeave,
  isParticipantView = false,
  joinDiagnostics = null,
  // The camera the browser opened for the join page's permission check. It's
  // the one the OS treats as the default, so it beats list order on a first
  // visit (KIN-2012).
  browserDefaultCameraId = null,
  // Supplied by the participant (patient) join page, which resolves them from its own public
  // join response since that caller has no authenticated session for usePracticeDetails below to
  // use. Left undefined on the staff side, which resolves its own practice name instead.
  practiceName: practiceNameProp,
  doctorName = null,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [isConnected, setIsConnected] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [error, setError] = useState(null);
  const [, setAudioPermissionGranted] = useState(false);
  const [microphoneLevel, setMicrophoneLevel] = useState(0);
  const [showDeviceSettings, setShowDeviceSettings] = useState(false);
  const [availableAudioInputs, setAvailableAudioInputs] = useState([]);
  const [availableVideoInputs, setAvailableVideoInputs] = useState([]);
  const [availableAudioOutputs, setAvailableAudioOutputs] = useState([]);
  const [selectedAudioInput, setSelectedAudioInput] = useState(null);
  const [selectedVideoInput, setSelectedVideoInput] = useState(null);
  const [selectedAudioOutput, setSelectedAudioOutput] = useState(null);
  const [isBlurEnabled, setIsBlurEnabled] = useState(false);
  const [isBlurSupported, setIsBlurSupported] = useState(false);
  // Shown in the meeting info bar and the local preview overlay. The staff side has an
  // authenticated session, so it resolves its own practice name here; the participant (patient)
  // side has none, so it relies entirely on the practiceNameProp its join page supplied instead —
  // this hook's query is simply disabled (no tenant) and contributes nothing there.
  const { data: practiceDetails } = usePracticeDetails();
  const practiceName = practiceNameProp || practiceDetails?.at(0)?.name || "";
  const [remoteSpeakerLevels, setRemoteSpeakerLevels] = useState({});
  const [remoteParticipantMuted, setRemoteParticipantMuted] = useState(false);
  const [audioElementState, setAudioElementState] = useState({
    hasStream: false,
    isPlaying: false,
    currentTime: 0,
    readyState: 0,
  });
  const [remoteAudioActive, setRemoteAudioActive] = useState(false);
  const [needsAudioUnmute, setNeedsAudioUnmute] = useState(false);
  const [microphoneStatus, setMicrophoneStatus] = useState("initializing");
  const [isRefreshingDevices, setIsRefreshingDevices] = useState(false);
  const [audioEnabledByUser, setAudioEnabledByUser] = useState(false);

  // Track narrow viewports so the participant-view audio-enable overlay
  // can shrink to fit on phones. Without this it stacks ~480px tall
  // (icon + heading + body + two device selects + button) and overflows
  // the visible area on portrait mobile.
  const [isNarrowViewport, setIsNarrowViewport] = useState(
    typeof window !== "undefined" && window.matchMedia
      ? window.matchMedia("(max-width: 600px)").matches
      : false
  );

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(max-width: 600px)");
    const update = (event) => setIsNarrowViewport(event.matches);
    setIsNarrowViewport(mq.matches);
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const remoteLabel = isParticipantView ? "Doctor" : "Patient";
  const localLabel = "You";
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);

  const meetingSessionRef = useRef(null);
  const audioElementRef = useRef(null);
  const videoElementRef = useRef(null);
  const remoteVideoElementRef = useRef(null);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const animationFrameRef = useRef(null);
  const shownToastsRef = useRef(new Set());
  const localVideoTileIdRef = useRef(null);
  const remoteVideoTileIdRef = useRef(null);
  const videoHealthWatchdogRef = useRef(null);
  const videoRecoveryAttemptsRef = useRef(0);
  const wakeLockRef = useRef(null);
  const [showReconnectingOverlay, setShowReconnectingOverlay] = useState(false);
  const reconnectingOverlayTimerRef = useRef(null);
  const chimeLoggerRef = useRef(null);
  const blurDeviceResolverRef = useRef(null);
  const callStatsRef = useRef(null);
  const isFinalSummarySentRef = useRef(false);
  // Chime's removeObserver() only removes the object it is given, so keep the
  // ones added; otherwise they keep firing through teardown (KIN-1987).
  const audioVideoObserversRef = useRef([]);
  const eventObserverRef = useRef(null);
  const mountedAtRef = useRef(Date.now());

  // Listeners registered once inside the meeting effect read these refs, so
  // they see the current value instead of the one from the first render.
  const isConnectedRef = useRef(isConnected);
  isConnectedRef.current = isConnected;
  const audioEnabledByUserRef = useRef(audioEnabledByUser);
  audioEnabledByUserRef.current = audioEnabledByUser;
  const selectedAudioInputRef = useRef(selectedAudioInput);
  selectedAudioInputRef.current = selectedAudioInput;
  const isMutedRef = useRef(isMuted);
  isMutedRef.current = isMuted;
  const isVideoOffRef = useRef(isVideoOff);
  isVideoOffRef.current = isVideoOff;

  const requestWakeLock = async () => {
    if (!("wakeLock" in navigator)) return;
    if (wakeLockRef.current) return;

    try {
      const sentinel = await navigator.wakeLock.request("screen");
      wakeLockRef.current = sentinel;
      sentinel.addEventListener("release", () => {
        if (wakeLockRef.current === sentinel) {
          wakeLockRef.current = null;
        }
      });
    } catch (error) {
      console.warn("Wake lock request failed:", error);
    }
  };

  const releaseWakeLock = async () => {
    const sentinel = wakeLockRef.current;
    if (!sentinel) return;

    wakeLockRef.current = null;
    try {
      await sentinel.release();
    } catch (error) {
      console.warn("Wake lock release failed:", error);
    }
  };
  const [audioVisualizerData, setAudioVisualizerData] = useState(
    new Array(16).fill(0)
  );

  // Resilience hook for automatic reconnection and monitoring
  const {
    networkQuality,
    isReconnecting,
    reconnectionAttempts,
    audioHealthy,
    setupMeetingResilience,
    createResilientObserver,
    bindAudioWithResilience,
    triggerReconnection,
    handleRemoteAttendeeLeft,
    reportError,
    cleanup: cleanupResilience,
  } = useVideoCallResilience({
    onReconnecting: (attempt) => {
      console.log(`Reconnection attempt ${attempt}`);
      callStatsRef.current?.noteReconnect();
    },
    onReconnected: () => {
      console.log("Reconnected successfully");
      setIsConnected(true);
    },
    onReconnectionFailed: () => {
      console.error("All reconnection attempts failed");
      setError("Connection lost. Please rejoin the meeting.");
    },
    onJoinedFromAnotherDevice: () => {
      setError(
        "This call was opened in another window or on another device, so it was closed here."
      );
    },
    // A reconnect restarts the mic the user was on, keeping their mute.
    getAudioInputState: () => ({
      deviceId: selectedAudioInputRef.current?.deviceId ?? null,
      isMuted: isMutedRef.current,
    }),
    // Audio is re-bound by audioVideoDidStart; the camera tile needs a nudge.
    onSessionRestarted: () => {
      if (!isVideoOffRef.current) {
        meetingSessionRef.current?.audioVideo.startLocalVideoTile();
      }
    },
    onNetworkQualityChange: (quality) => {
      const detail = getConnectionQualityDetail(quality);
      if (detail) {
        console.log(`[video-call] ${detail}`);
      } else {
        console.log(`[video-call] network quality changed: ${quality}`);
      }
    },
    onAudioRecovered: () => {
      console.log("Audio recovered");
    },
    onDeviceChanged: async (devices) => {
      // Update available devices when they change
      setAvailableAudioInputs(devices.audioInputs || []);
      setAvailableVideoInputs(devices.videoInputs || []);
      setAvailableAudioOutputs(devices.audioOutputs || []);
    },
  });

  const reportErrorRef = useRef(reportError);
  reportErrorRef.current = reportError;

  // Read once per call so it can't change mid-meeting.
  const isDiagnosticsEnabledRef = useRef(isDiagnosticsReportingEnabled());

  const sendDiagnostic = (errorType, details, recentLogs) => {
    if (!isDiagnosticsEnabledRef.current) return;

    try {
      const context = {
        ...details,
        participantView: isParticipantView,
        msSinceMount: Date.now() - mountedAtRef.current,
        msSinceJoinClick: joinDiagnostics?.requestedAt
          ? Date.now() - joinDiagnostics.requestedAt
          : null,
        microphonePermissionBeforeJoin:
          joinDiagnostics?.microphonePermissionBeforeJoin ?? null,
      };

      reportErrorRef.current(errorType, buildReportMessage(context, recentLogs));
    } catch (error) {
      console.warn(`Failed to build video diagnostic "${errorType}":`, error);
    }
  };

  // Normal milestones, so failing calls can be compared against working ones.
  const reportDiagnostic = (errorType, details = {}) =>
    sendDiagnostic(errorType, details, []);

  const reportProblem = (errorType, details = {}) =>
    sendDiagnostic(
      errorType,
      details,
      chimeLoggerRef.current?.recentEntries() || []
    );

  // One per call when it ends, plus a snapshot whenever the tab is hidden:
  // patients often end a call by closing the tab, and a request started while
  // the page is unloading can be lost, so the last snapshot stands in for it.
  const sendCallSummary = ({ isFinal }) => {
    if (!callStatsRef.current || isFinalSummarySentRef.current) return;

    if (isFinal) isFinalSummarySentRef.current = true;
    reportDiagnostic("call_summary", {
      ...callStatsRef.current.summarise(),
      isFinal,
    });
  };

  const reportPlaybackBlocked = async (stage, playError) => {
    if (!isDiagnosticsEnabledRef.current) return;

    reportProblem("audio_autoplay_blocked", {
      stage,
      error: describeError(playError),
      playback: describeAudioPlayback(
        audioElementRef.current,
        audioContextRef.current
      ),
      microphonePermission: await getMicrophonePermissionState(),
    });
  };

  // Debounce the full-screen "Reconnecting…" overlay. Chime's internal
  // reconnection often completes in <1s; flashing the overlay for those is
  // disconcerting. We only show it once a reconnection has been running
  // for more than RECONNECTING_OVERLAY_DELAY_MS.
  useEffect(() => {
    const RECONNECTING_OVERLAY_DELAY_MS = 1200;

    if (isReconnecting) {
      if (!reconnectingOverlayTimerRef.current) {
        reconnectingOverlayTimerRef.current = setTimeout(() => {
          setShowReconnectingOverlay(true);
          reconnectingOverlayTimerRef.current = null;
        }, RECONNECTING_OVERLAY_DELAY_MS);
      }
    } else {
      if (reconnectingOverlayTimerRef.current) {
        clearTimeout(reconnectingOverlayTimerRef.current);
        reconnectingOverlayTimerRef.current = null;
      }
      setShowReconnectingOverlay(false);
    }

    return () => {
      if (reconnectingOverlayTimerRef.current) {
        clearTimeout(reconnectingOverlayTimerRef.current);
        reconnectingOverlayTimerRef.current = null;
      }
    };
  }, [isReconnecting]);

  const showToastOnce = (id, message, type = "success") => {
    if (shownToastsRef.current.has(id)) {
      return;
    }
    shownToastsRef.current.add(id);
    const toastOptions = { id, duration: 1000 };
    if (type === "success") {
      toast.success(message, toastOptions);
    } else if (type === "error") {
      toast.error(message, toastOptions);
    }
  };

  const STORAGE_KEYS = {
    audioInput: "kinesin_preferred_audio_input",
    videoInput: "kinesin_preferred_video_input",
    audioOutput: "kinesin_preferred_audio_output",
    videoBlur: "kinesin_preferred_video_blur",
  };

  const saveDevicePreference = (key, deviceId) => {
    try {
      localStorage.setItem(key, deviceId);
    } catch (e) {
      console.warn("Failed to save device preference:", e);
    }
  };

  const getSavedDevicePreference = (key) => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      console.warn("Failed to get device preference:", e);
      return null;
    }
  };

  // Loaded once: the saved blur preference is read before the meeting session exists, so the
  // very first `startVideoInput` call (on join) already applies it. Background blur is a
  // doctor-only feature — skipped entirely on a participant (patient) join, even if this device's
  // localStorage has a blur preference saved from a doctor's session on the same machine.
  useEffect(() => {
    if (
      !isParticipantView &&
      getSavedDevicePreference(STORAGE_KEYS.videoBlur) === "1"
    ) {
      setIsBlurEnabled(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const findSavedDevice = (devices, savedDeviceId) => {
    if (!savedDeviceId || !devices || devices.length === 0) return null;
    return devices.find((d) => d.deviceId === savedDeviceId) || null;
  };

  // Devices whose label or deviceId contains "default" / "communications"
  // are OS-level virtual aliases that frequently misroute audio (especially
  // on Windows). Prefer a real, named device when one exists.
  const isDefaultLikeDevice = (device) => {
    if (!device) return false;
    const label = (device.label || "").toLowerCase();
    const id = (device.deviceId || "").toLowerCase();
    return (
      label.includes("default") ||
      label.includes("communications") ||
      id === "default" ||
      id === "communications"
    );
  };

  const pickPreferredDevice = (devices) => {
    if (!devices || devices.length === 0) return null;
    const real = devices.find((d) => !isDefaultLikeDevice(d));
    return real || devices[0];
  };

  // iOS Safari does not implement HTMLMediaElement.setSinkId, so
  // chooseAudioOutput is a no-op there. listAudioOutputDevices() also
  // returns []. We synthesize Speakerphone/Earpiece options so the user
  // still has a control affordance, and apply a rebind workaround that
  // nudges Safari toward speaker routing.
  const IOS_SPEAKER_DEVICE = {
    deviceId: "ios-speaker",
    label: "Speakerphone",
    isSynthetic: true,
  };
  const IOS_EARPIECE_DEVICE = {
    deviceId: "ios-earpiece",
    label: "Earpiece (Phone)",
    isSynthetic: true,
  };
  const IOS_SPEAKER_OPTIONS = [IOS_SPEAKER_DEVICE, IOS_EARPIECE_DEVICE];

  // Try to coax iOS Safari to re-route remote audio to the loudspeaker by
  // tearing down and re-binding the <audio> element under a fresh user
  // gesture. There is no reliable programmatic switch on iOS — this works
  // some of the time and we surface a hint to the user either way.
  const applyIOSAudioRouteWorkaround = async () => {
    const audioElement = audioElementRef.current;
    const meetingSession = meetingSessionRef.current;
    if (!audioElement || !meetingSession) return;

    try {
      audioElement.pause();
      audioElement.srcObject = null;
      // Safari 17+ needs ~120-150ms for the OS audio session to re-evaluate
      // routing; the previous 60ms gap was too tight and the rebind often
      // landed on the old sink.
      await new Promise((resolve) => setTimeout(resolve, 140));
      await meetingSession.audioVideo.bindAudioElement(audioElement);
      audioElement.muted = false;
      audioElement.volume = 1.0;
      await audioElement.play();
    } catch (e) {
      // The element was paused above, so without the banner the remote
      // audio would stay silent with no way for the patient to restart it.
      console.warn("iOS audio route workaround failed:", e);
      setNeedsAudioUnmute(true);
      reportPlaybackBlocked("ios_route_workaround", e);
    }
  };

  const cleanupMeeting = async () => {
    sendCallSummary({ isFinal: true });

    // Cleanup resilience monitors first
    cleanupResilience();

    await releaseWakeLock();

    if (videoHealthWatchdogRef.current) {
      clearTimeout(videoHealthWatchdogRef.current);
      videoHealthWatchdogRef.current = null;
    }
    localVideoTileIdRef.current = null;
    remoteVideoTileIdRef.current = null;
    videoRecoveryAttemptsRef.current = 0;

    if (meetingSessionRef.current) {
      try {
        meetingSessionRef.current.audioVideo.stopLocalVideoTile();

        await meetingSessionRef.current.audioVideo.stopAudioInput();

        await meetingSessionRef.current.audioVideo.stopVideoInput();

        await blurDeviceResolverRef.current?.dispose();

        meetingSessionRef.current.audioVideo.realtimeUnsubscribeFromVolumeIndicator(
          meetingSessionRef.current.configuration.credentials.attendeeId
        );

        audioVideoObserversRef.current.forEach((observer) =>
          meetingSessionRef.current.audioVideo.removeObserver(observer)
        );
        audioVideoObserversRef.current = [];

        if (eventObserverRef.current) {
          meetingSessionRef.current.eventController?.removeObserver(
            eventObserverRef.current
          );
          eventObserverRef.current = null;
        }

        meetingSessionRef.current.audioVideo.stop();

        setIsMuted(false);
        setIsVideoOff(false);
        setMicrophoneLevel(0);
        setIsConnected(false);
        setAudioPermissionGranted(false);
      } catch (err) {
        console.error("Error during meeting cleanup:", err);
      }
    }
  };

  const checkMicrophonePermissions = async () => {
    try {
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error(
          "Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Safari."
        );
      }

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: true,
        video: false,
      });

      const audioTracks = stream.getAudioTracks();
      if (audioTracks.length === 0) {
        stream.getTracks().forEach((track) => track.stop());
        throw new Error(
          "No microphone tracks available. Please check your microphone connection."
        );
      }

      stream.getTracks().forEach((track) => track.stop());
      setAudioPermissionGranted(true);
      return true;
    } catch (error) {
      console.error("Microphone permission check failed:", error);
      setAudioPermissionGranted(false);

      if (error.name === "NotAllowedError") {
        throw new Error(
          "Microphone access denied. Please click the microphone icon in your browser's address bar and allow microphone access, then refresh the page."
        );
      } else if (error.name === "NotFoundError") {
        throw new Error(
          "No microphone found. Please connect a microphone to your device and try again."
        );
      } else if (error.name === "NotReadableError") {
        throw new Error(
          "Microphone is already in use by another application. Please close other applications using the microphone and try again."
        );
      } else if (error.name === "OverconstrainedError") {
        throw new Error(
          "No suitable microphone found. Please check your microphone settings."
        );
      } else if (error.name === "AbortError") {
        throw new Error("Microphone access was interrupted. Please try again.");
      } else {
        throw new Error(`Microphone error: ${error.message}`);
      }
    }
  };

  const verifyAudioTransmission = async () => {
    const audioElement = audioElementRef.current;
    if (!audioElement) {
      console.warn("⚠️ Audio element not found");
      return;
    }

    const monitorAudioElement = () => {
      const state = {
        hasStream: !!audioElement.srcObject,
        isPlaying: !audioElement.paused,
        currentTime: audioElement.currentTime,
        readyState: audioElement.readyState,
        volume: audioElement.volume,
        muted: audioElement.muted,
      };

      setAudioElementState(state);

      if (state.hasStream) {
        const stream = audioElement.srcObject;
        if (stream && stream.getAudioTracks) {
          stream.getAudioTracks();
        }

        // iOS Safari can silently pause or mute the audio element after a
        // phone-call interruption, Siri activation, or audio-session
        // reroute. When that happens mid-call, re-surface the tap-to-enable
        // banner so the user has a one-tap recovery path.
        if (
          audioEnabledByUserRef.current &&
          (audioElement.paused || audioElement.muted)
        ) {
          setNeedsAudioUnmute(true);
        }
      }
    };

    // Build the analyser ONLY when Chime has attached a MediaStream to the
    // <audio> element's srcObject. We must never call
    // createMediaElementSource on the Chime audio element — it reroutes
    // the element through Web Audio and (without an explicit connection
    // to audioContext.destination) silences the remote participant for
    // the entire call. Bailing out here lets the listeners below retry
    // once Chime binds the stream.
    const setupAudioVisualizer = () => {
      if (analyserRef.current) {
        return;
      }
      const stream = audioElement.srcObject;
      if (!stream || typeof stream.getAudioTracks !== "function") {
        return;
      }
      if (stream.getAudioTracks().length === 0) {
        return;
      }

      try {
        const AudioContextCtor =
          window.AudioContext || window.webkitAudioContext;
        const audioContext =
          audioContextRef.current ||
          new AudioContextCtor({ latencyHint: "interactive" });
        audioContextRef.current = audioContext;

        const analyser = audioContext.createAnalyser();
        analyser.fftSize = 64;
        analyser.smoothingTimeConstant = 0.8;

        const source = audioContext.createMediaStreamSource(stream);
        source.connect(analyser);

        analyserRef.current = analyser;

        const updateVisualizer = () => {
          if (!analyserRef.current) return;

          const dataArray = new Uint8Array(
            analyserRef.current.frequencyBinCount
          );
          analyserRef.current.getByteFrequencyData(dataArray);

          const normalizedData = Array.from(dataArray.slice(0, 16)).map((v) =>
            Math.round((v / 255) * 100)
          );
          setAudioVisualizerData(normalizedData);

          const hasAudio = normalizedData.some((v) => v > 5);
          setRemoteAudioActive(hasAudio);

          animationFrameRef.current = requestAnimationFrame(updateVisualizer);
        };

        updateVisualizer();
      } catch (error) {
        console.error("❌ Error setting up audio visualizer:", error);
        analyserRef.current = null;
      }
    };

    setTimeout(() => {
      monitorAudioElement();
      setupAudioVisualizer();
    }, 2000);

    const monitorInterval = setInterval(monitorAudioElement, 5000);

    audioElement.addEventListener("playing", () => {
      setAudioElementState((prev) => ({ ...prev, isPlaying: true }));
      setupAudioVisualizer();
    });

    audioElement.addEventListener("pause", () => {
      setAudioElementState((prev) => ({ ...prev, isPlaying: false }));
    });

    audioElement.addEventListener("loadeddata", () => {
      setupAudioVisualizer();
    });

    return () => {
      clearInterval(monitorInterval);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      analyserRef.current = null;
      if (audioContextRef.current) {
        audioContextRef.current.close().catch(() => {});
        audioContextRef.current = null;
      }
    };
  };

  const loadAvailableDevices = async (meetingSession) => {
    try {
      const audioInputDevices =
        await meetingSession.audioVideo.listAudioInputDevices();
      const videoInputDevices =
        await meetingSession.audioVideo.listVideoInputDevices();
      const audioOutputDevices =
        await meetingSession.audioVideo.listAudioOutputDevices();

      if (audioInputDevices.length > 0) {
        setAvailableAudioInputs(audioInputDevices);
      }
      if (videoInputDevices.length > 0) {
        setAvailableVideoInputs(videoInputDevices);
      }
      if (audioOutputDevices.length > 0) {
        setAvailableAudioOutputs(audioOutputDevices);
      }

      const savedAudioInputId = getSavedDevicePreference(
        STORAGE_KEYS.audioInput
      );
      const savedVideoInputId = getSavedDevicePreference(
        STORAGE_KEYS.videoInput
      );
      const savedAudioOutputId = getSavedDevicePreference(
        STORAGE_KEYS.audioOutput
      );

      if (audioInputDevices.length > 0 && !selectedAudioInput) {
        const savedDevice = findSavedDevice(
          audioInputDevices,
          savedAudioInputId
        );
        setSelectedAudioInput(
          savedDevice || pickPreferredDevice(audioInputDevices)
        );
      }
      if (videoInputDevices.length > 0 && !selectedVideoInput) {
        const savedDevice = findSavedDevice(
          videoInputDevices,
          savedVideoInputId
        );
        setSelectedVideoInput(
          savedDevice ||
            pickCameraDevice(videoInputDevices, {
              browserDefaultDeviceId: browserDefaultCameraId,
            })
        );
      }
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      if (!isIOS && audioOutputDevices.length > 0 && !selectedAudioOutput) {
        const savedDevice = findSavedDevice(
          audioOutputDevices,
          savedAudioOutputId
        );
        setSelectedAudioOutput(
          savedDevice || pickPreferredDevice(audioOutputDevices)
        );
      }
    } catch (error) {
      console.error("Failed to load devices:", error);
    }
  };

  const changeAudioInputDevice = async (device) => {
    if (!meetingSessionRef.current) return;

    const audioVideo = meetingSessionRef.current.audioVideo;

    try {
      // Some USB mic + Chrome combinations (notably Rode) leave the
      // previous RTCRtpSender pointing at a stale track when you switch
      // without an explicit stop first, which surfaces in the SDK as
      // "Sending Audio is unhealthy" with `packetsSent` stuck at 0. A
      // clean stopAudioInput() forces the SDK to drop the old track and
      // re-attach the new one to the peer connection.
      try {
        await audioVideo.stopAudioInput();
      } catch (stopError) {
        console.warn(
          "stopAudioInput before device switch failed (continuing):",
          stopError
        );
      }

      await audioVideo.startAudioInput(device.deviceId);
      setSelectedAudioInput(device);
      saveDevicePreference(STORAGE_KEYS.audioInput, device.deviceId);

      // Re-assert mute state — stopAudioInput resets it to unmuted.
      if (isMuted) {
        audioVideo.realtimeMuteLocalAudio();
      }
    } catch (error) {
      console.error("Failed to change audio device:", error);
      toast.error(
        "Couldn't switch to that microphone. Try another input, or unplug/replug the device.",
        { id: "mic-switch-failed", duration: 5000 }
      );
    }
  };

  const changeVideoInputDevice = async (device) => {
    if (!meetingSessionRef.current) return;

    try {
      const resolvedDevice = await blurDeviceResolverRef.current.resolveDevice(
        device.deviceId,
        isBlurEnabled
      );
      await meetingSessionRef.current.audioVideo.startVideoInput(
        resolvedDevice
      );
      setSelectedVideoInput(device);
      saveDevicePreference(STORAGE_KEYS.videoInput, device.deviceId);
    } catch (error) {
      console.error("Failed to change video device:", error);
      toast.error("Failed to change camera", { duration: 1000 });
    }
  };

  /**
   * Flips background blur on/off. If a call is already running with video on, re-applies
   * immediately so the toggle takes visible effect without the user having to restart their
   * camera; otherwise it's simply picked up the next time video starts.
   */
  const toggleBackgroundBlur = async () => {
    const newValue = !isBlurEnabled;
    setIsBlurEnabled(newValue);
    saveDevicePreference(STORAGE_KEYS.videoBlur, newValue ? "1" : "0");

    if (!meetingSessionRef.current || isVideoOff || !selectedVideoInput) {
      return;
    }

    try {
      const device = await blurDeviceResolverRef.current.resolveDevice(
        selectedVideoInput.deviceId,
        newValue
      );
      await meetingSessionRef.current.audioVideo.startVideoInput(device);
    } catch (error) {
      console.error("Failed to toggle background blur:", error);
      toast.error("Failed to toggle background blur", { duration: 1500 });
      setIsBlurEnabled(!newValue);
    }
  };

  const changeAudioOutputDevice = async (device) => {
    if (!meetingSessionRef.current || !device) return;

    // iOS synthetic options: there is no setSinkId on iOS Safari, so we
    // attempt the rebind workaround and tell the user how to confirm the
    // route via Control Center.
    if (device.isSynthetic) {
      await applyIOSAudioRouteWorkaround();
      setSelectedAudioOutput(device);
      saveDevicePreference(STORAGE_KEYS.audioOutput, device.deviceId);
      return;
    }

    try {
      await meetingSessionRef.current.audioVideo.chooseAudioOutput(
        device.deviceId
      );
      setSelectedAudioOutput(device);
      saveDevicePreference(STORAGE_KEYS.audioOutput, device.deviceId);
    } catch (error) {
      console.error("Failed to change audio output device:", error);
      toast.error("Speaker selection not supported on this browser", {
        duration: 1000,
      });
    }
  };

  const isMobileDevice = () => {
    return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  };

  const isIOSDevice = () => {
    return /iPhone|iPad|iPod/i.test(navigator.userAgent);
  };

  const refreshDeviceList = async () => {
    if (!meetingSessionRef.current) {
      console.warn("Cannot refresh devices - no meeting session");
      return;
    }

    setIsRefreshingDevices(true);

    try {
      const isIOS = isIOSDevice();

      if (isIOS) {
        try {
          const stream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: true,
          });
          await new Promise((resolve) => setTimeout(resolve, 500));
          stream.getTracks().forEach((track) => track.stop());
        } catch (e) {}
      }

      const chimeAudioInputs =
        await meetingSessionRef.current.audioVideo.listAudioInputDevices();
      const chimeVideoInputs =
        await meetingSessionRef.current.audioVideo.listVideoInputDevices();
      const chimeAudioOutputs =
        await meetingSessionRef.current.audioVideo.listAudioOutputDevices();

      if (chimeAudioInputs.length > 0) {
        setAvailableAudioInputs(chimeAudioInputs);
        const currentInList = chimeAudioInputs.find(
          (d) => d.deviceId === selectedAudioInput?.deviceId
        );
        if (!currentInList && chimeAudioInputs.length > 0) {
          setSelectedAudioInput(pickPreferredDevice(chimeAudioInputs));
        }
      }

      if (chimeVideoInputs.length > 0) {
        setAvailableVideoInputs(chimeVideoInputs);
      }

      if (chimeAudioOutputs.length > 0) {
        setAvailableAudioOutputs(chimeAudioOutputs);
      }
    } catch (error) {
      console.error("Failed to refresh device list:", error);
      toast.error("Failed to refresh devices", { duration: 1000 });
    } finally {
      setIsRefreshingDevices(false);
    }
  };

  const initializeAudioDevices = async (meetingSession) => {
    try {
      setMicrophoneStatus("initializing");
      const isMobile = isMobileDevice();
      const isIOS = isIOSDevice();

      if (isIOS) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
      }

      let audioInputDevices =
        await meetingSession.audioVideo.listAudioInputDevices();

      if (isIOS && audioInputDevices.length <= 1) {
        try {
          const nativeDevices = await navigator.mediaDevices.enumerateDevices();
          const nativeAudioInputs = nativeDevices.filter(
            (d) => d.kind === "audioinput"
          );

          if (nativeAudioInputs.length > audioInputDevices.length) {
            await new Promise((resolve) => setTimeout(resolve, 500));
            audioInputDevices =
              await meetingSession.audioVideo.listAudioInputDevices();
          }
        } catch (e) {}
      }

      if (audioInputDevices.length === 0) {
        setMicrophoneStatus("error");
        throw new Error(
          "No microphone devices found. Please connect a microphone and refresh the page."
        );
      }

      const audioOutputDevices =
        await meetingSession.audioVideo.listAudioOutputDevices();

      const audioElement = audioElementRef.current;
      if (audioElement) {
        audioElement.autoplay = true;
        audioElement.playsInline = true;
        audioElement.muted = false;
        audioElement.volume = 1.0;
        audioElement.preservesPitch = false;
        audioElement.disableRemotePlayback = true;

        // Initial audio bind — the audioVideoDidStart observer will
        // re-bind with resilience, but we do a first pass here so the
        // element is ready as early as possible.
        try {
          await meetingSession.audioVideo.bindAudioElement(audioElement);
        } catch (bindError) {
          console.warn("Initial audio bind failed, will retry in audioVideoDidStart:", bindError);
        }

        // play() is deliberately left to audioVideoDidStart: by then the
        // remote stream exists and the microphone is capturing, which is
        // what lets Safari allow audible playback long after the Join click.
      }

      let micStarted = false;
      let startedAudioInput = null;
      let chosenAudioOutput = null;

      const savedAudioInputId = getSavedDevicePreference(
        STORAGE_KEYS.audioInput
      );
      const savedAudioDevice = findSavedDevice(
        audioInputDevices,
        savedAudioInputId
      );

      // Try real (non-"Default"/Communications) devices first, then any
      // remaining devices. OS-virtual aliases like "Default" / "Communications"
      // often misroute audio, so we only fall back to them if nothing else
      // works.
      const orderedAudioInputs = [
        ...audioInputDevices.filter((d) => !isDefaultLikeDevice(d)),
        ...audioInputDevices.filter((d) => isDefaultLikeDevice(d)),
      ];

      if (isIOS || isMobile) {
        // On iOS WebKit defaults aren't always ideal — explicit constraints
        // pin echo-cancellation, noise suppression and AGC ON and force a
        // mono channel, which materially cleans up speakerphone echo and
        // BT-headset AGC pumping.
        const mobileAudioConstraints = {
          echoCancellation: { ideal: true },
          noiseSuppression: { ideal: true },
          autoGainControl: { ideal: true },
          channelCount: { ideal: 1 },
        };

        const startMobileMic = async (deviceId) => {
          await meetingSession.audioVideo.startAudioInput({
            deviceId: deviceId ? { exact: deviceId } : undefined,
            ...mobileAudioConstraints,
          });
        };

        if (savedAudioDevice) {
          try {
            await startMobileMic(savedAudioDevice.deviceId);
            setSelectedAudioInput(savedAudioDevice);
            startedAudioInput = savedAudioDevice;

            micStarted = true;
          } catch (savedError) {}
        }

        if (!micStarted) {
          for (const device of orderedAudioInputs) {
            try {
              await startMobileMic(device.deviceId);
              setSelectedAudioInput(device);
              startedAudioInput = device;

              micStarted = true;
              break;
            } catch (deviceError) {}
          }
        }

        if (!micStarted) {
          try {
            await meetingSession.audioVideo.startAudioInput(
              mobileAudioConstraints
            );
            micStarted = true;
          } catch (constrainedError) {
            try {
              await meetingSession.audioVideo.startAudioInput({ audio: true });
              micStarted = true;
            } catch (defaultError) {
              console.error(
                "Default audio constraints also failed:",
                defaultError
              );
            }
          }
        }
      } else {
        if (savedAudioDevice) {
          try {
            await meetingSession.audioVideo.startAudioInput(
              savedAudioDevice.deviceId
            );
            setSelectedAudioInput(savedAudioDevice);
            startedAudioInput = savedAudioDevice;

            micStarted = true;
          } catch (savedError) {}
        }

        if (!micStarted) {
          for (const device of orderedAudioInputs) {
            try {
              await meetingSession.audioVideo.startAudioInput(device.deviceId);
              setSelectedAudioInput(device);
              startedAudioInput = device;

              micStarted = true;
              break;
            } catch (deviceError) {}
          }
        }

        if (!micStarted) {
          try {
            await meetingSession.audioVideo.startAudioInput(null);

            micStarted = true;
          } catch (_nullError) {}
        }
      }

      if (!micStarted) {
        setMicrophoneStatus("error");
        toast.error(
          "Couldn't access your microphone. Check that no other app (Zoom, Teams, etc.) has it open, then refresh.",
          { id: "mic-start-failed", duration: 6000 }
        );
      } else {
        setMicrophoneStatus("active");
      }

      if (isIOS) {
        // iOS Safari does not expose listAudioOutputDevices / setSinkId.
        // Seed the speaker selection with the synthetic Speakerphone option
        // so the UI reflects something and the user can attempt to switch.
        const savedAudioOutputId = getSavedDevicePreference(
          STORAGE_KEYS.audioOutput
        );
        const savedSynthetic = IOS_SPEAKER_OPTIONS.find(
          (d) => d.deviceId === savedAudioOutputId
        );
        setSelectedAudioOutput(savedSynthetic || IOS_SPEAKER_DEVICE);
      } else if (audioOutputDevices.length > 0) {
        const savedAudioOutputId = getSavedDevicePreference(
          STORAGE_KEYS.audioOutput
        );
        const savedOutputDevice = findSavedDevice(
          audioOutputDevices,
          savedAudioOutputId
        );
        const outputDeviceToUse =
          savedOutputDevice || pickPreferredDevice(audioOutputDevices);

        try {
          await meetingSession.audioVideo.chooseAudioOutput(
            outputDeviceToUse.deviceId
          );
          setSelectedAudioOutput(outputDeviceToUse);
          chosenAudioOutput = outputDeviceToUse;
          if (savedOutputDevice) {
          } else {
          }
        } catch (outputError) {
          reportProblem("audio_output_select_failed", {
            error: describeError(outputError),
          });
        }
      } else {
      }

      setAvailableAudioInputs(audioInputDevices);
      setAvailableAudioOutputs(audioOutputDevices);

      const describeDevice = (device) =>
        device
          ? { label: device.label, isDefaultLike: isDefaultLikeDevice(device) }
          : null;
      reportDiagnostic("audio_devices_selected", {
        microphoneStarted: micStarted,
        microphone: describeDevice(startedAudioInput),
        speaker: describeDevice(chosenAudioOutput),
        usedSavedMicrophone:
          !!savedAudioDevice && startedAudioInput === savedAudioDevice,
        audioInputCount: audioInputDevices.length,
        audioOutputCount: audioOutputDevices.length,
      });

      if (isIOS) {
        setTimeout(() => {
          refreshDeviceList();
        }, 3000);
      }

      return true;
    } catch (error) {
      console.error("Audio device initialization failed:", error);
      setMicrophoneStatus("error");

      if (
        error.message.includes("Permission denied") ||
        error.name === "NotAllowedError"
      ) {
        throw new Error(
          "Audio permission denied. Please allow microphone access and refresh the page."
        );
      } else if (
        error.message.includes("Could not start audio input") ||
        error.message.includes("startAudioInput")
      ) {
        throw new Error(
          "Failed to start microphone. Please check your audio settings and ensure no other application is using your microphone."
        );
      } else {
        throw new Error(`Audio initialization failed: ${error.message}`);
      }
    }
  };

  useEffect(() => {
    if (!credentials || !meetingId) {
      setError("No meeting credentials provided");
      setIsLoading(false);
      return;
    }

    const initializeChimeMeeting = async () => {
      try {
        // Check WebRTC support first
        const webRTCSupport = checkWebRTCSupport();
        if (!webRTCSupport.isSupported) {
          throw new Error(
            `Your browser is missing required features: ${webRTCSupport.missingFeatures.join(
              ", "
            )}. Please use a modern browser like Chrome, Firefox, or Safari.`
          );
        }

        const meeting = credentials.Meeting ||
          credentials.meeting || {
            MeetingId: credentials.meetingId,
            ExternalMeetingId: credentials.externalMeetingId,
            MediaRegion: credentials.mediaRegion,
            MediaPlacement: credentials.MediaPlacement ||
              credentials.mediaPlacement || {
                AudioHostUrl: credentials.audioHostUrl,
                AudioFallbackUrl: credentials.audioFallbackUrl,
                SignalingUrl: credentials.signalingUrl,
                TurnControlUrl: credentials.turnControlUrl,
                ScreenDataUrl: credentials.screenDataUrl,
                ScreenSharingUrl: credentials.screenSharingUrl,
                ScreenViewingUrl: credentials.screenViewingUrl,
              },
          };

        const attendee = credentials.Attendee ||
          credentials.attendee || {
            AttendeeId: credentials.attendeeId,
            ExternalUserId: credentials.externalUserId,
            JoinToken: credentials.joinToken,
          };

        const currentMeetingId = meeting.MeetingId || credentials.meetingId;
        const currentAttendeeId = attendee.AttendeeId || credentials.attendeeId;
        const currentJoinToken = attendee.JoinToken || credentials.joinToken;

        if (!currentMeetingId) {
          throw new Error("Missing meeting ID in credentials");
        }

        if (!currentAttendeeId || !currentJoinToken) {
          throw new Error("Missing attendee credentials");
        }

        const mediaPlacement = meeting.MediaPlacement;

        if (!mediaPlacement?.AudioHostUrl) {
          const errMsg = "Missing AudioHostUrl - audio will not work";
          console.error("❌", errMsg);
          toast.error(errMsg);
          throw new Error(errMsg);
        }

        if (!mediaPlacement?.SignalingUrl) {
          const errMsg = "Missing SignalingUrl - connection will fail";
          console.error("❌", errMsg);
          toast.error(errMsg);
          throw new Error(errMsg);
        }

        // The console stays at WARN, keeping actual problems (e.g. "Sending
        // Audio is unhealthy") visible while silencing the per-tick INFO
        // firehose (received-new-index, should-resubscribe, clock-skew
        // estimates). INFO lines are still kept in memory and attached to
        // problem reports.
        const logger = new BufferedChimeLogger("ChimeMeeting");
        chimeLoggerRef.current = logger;
        callStatsRef.current = new CallStatsCollector();
        isFinalSummarySentRef.current = false;

        blurDeviceResolverRef.current = createBlurDeviceResolver(logger);
        // Doctor-only feature: skip the (non-trivial) WASM/WebGL support probe entirely on a
        // participant (patient) join, where the toggle is never shown.
        if (!isParticipantView) {
          isBackgroundBlurSupported(logger).then(setIsBlurSupported);
        }

        const deviceController = new DefaultDeviceController(logger);

        const configuration = new MeetingSessionConfiguration(
          meeting,
          attendee
        );

        const meetingSession = new DefaultMeetingSession(
          configuration,
          logger,
          deviceController
        );

        meetingSessionRef.current = meetingSession;

        // Initialize resilience monitoring with meeting info for backend API calls
        await setupMeetingResilience(meetingSession, audioElementRef.current, {
          meetingId: currentMeetingId,
          attendeeId: currentAttendeeId,
          externalUserId: attendee.ExternalUserId || credentials.externalUserId,
          appointmentId: credentials.appointmentId,
        });

        // Create resilient observer with video tile handling
        const resilientObserver = createResilientObserver({
          metricsDidReceive: (clientMetricReport) => {
            callStatsRef.current?.record(
              clientMetricReport?.getObservableMetrics?.()
            );
          },
          videoTileDidUpdate: (tileState) => {
            if (!tileState.boundAttendeeId) {
              return;
            }

            const isLocal = tileState.localTile;
            callStatsRef.current?.noteVideoTile({ isLocal });
            const elementRef = isLocal ? videoElementRef : remoteVideoElementRef;
            const tileIdRef = isLocal
              ? localVideoTileIdRef
              : remoteVideoTileIdRef;
            const label = isLocal ? "local" : "remote";

            // If a previous tile was bound and Chime gave us a new tileId,
            // unbind the old one and clear the element so we don't leak
            // a stale stream (causes the "black screen after toggle" bug).
            if (
              tileIdRef.current &&
              tileIdRef.current !== tileState.tileId
            ) {
              try {
                meetingSession.audioVideo.unbindVideoElement(
                  tileIdRef.current
                );
              } catch (e) {
                console.warn(`Failed to unbind previous ${label} tile:`, e);
              }
              if (elementRef.current) {
                elementRef.current.srcObject = null;
              }
            }

            const bindVideoWithRetry = (tileId) => {
              const tryBind = (attempt = 0) => {
                if (elementRef.current) {
                  meetingSession.audioVideo.bindVideoElement(
                    tileId,
                    elementRef.current
                  );
                  tileIdRef.current = tileId;
                  if (isLocal) {
                    // Successful bind — schedule a watchdog to verify the
                    // local stream is actually producing frames.
                    scheduleLocalVideoHealthCheck();
                  }
                } else if (attempt < 5) {
                  setTimeout(() => tryBind(attempt + 1), 200);
                } else {
                  console.warn(
                    `Failed to bind ${label} video after ${attempt} attempts`
                  );
                }
              };
              tryBind();
            };

            bindVideoWithRetry(tileState.tileId);
          },
          videoTileWasRemoved: (tileId) => {
            // Clear srcObject + tracked tileId so the NEXT bind starts clean.
            if (localVideoTileIdRef.current === tileId) {
              try {
                meetingSession.audioVideo.unbindVideoElement(tileId);
              } catch (e) {
                console.warn("Failed to unbind local tile on remove:", e);
              }
              if (videoElementRef.current) {
                videoElementRef.current.srcObject = null;
              }
              localVideoTileIdRef.current = null;
            } else if (remoteVideoTileIdRef.current === tileId) {
              try {
                meetingSession.audioVideo.unbindVideoElement(tileId);
              } catch (e) {
                console.warn("Failed to unbind remote tile on remove:", e);
              }
              if (remoteVideoElementRef.current) {
                remoteVideoElementRef.current.srcObject = null;
              }
              remoteVideoTileIdRef.current = null;
            }
          },
          videoSendDidBecomeUnavailable: () => {
            // SDK says we can't send video right now — try to recover if the
            // user expects video to be on.
            console.warn("videoSendDidBecomeUnavailable — attempting recovery");
            recoverLocalVideo("send_unavailable");
          },
        });

        const addAudioVideoObserver = (observer) => {
          meetingSession.audioVideo.addObserver(observer);
          audioVideoObserversRef.current.push(observer);
        };

        addAudioVideoObserver(resilientObserver);

        // Audio level observer for volume indicators and additional monitoring
        const audioLevelObserver = {
          audioVideoDidStart: async () => {
            // Re-bind audio with resilience (retry + monitoring).
            try {
              const result = await bindAudioWithResilience(
                meetingSession,
                audioElementRef.current
              );

              if (result.needsUserInteraction) {
                setNeedsAudioUnmute(true);
                reportPlaybackBlocked("meeting_start_mobile", result.playError);
              } else {
                // Only clear the banner if play() actually succeeds now
                const audioEl = audioElementRef.current;

                if (audioEl && audioEl.paused) {
                  try {
                    await audioEl.play();
                    setNeedsAudioUnmute(false);
                  } catch (playError) {
                    setNeedsAudioUnmute(true);
                    reportPlaybackBlocked("meeting_start", playError);
                  }
                } else {
                  setNeedsAudioUnmute(false);
                }
              }

              setAudioElementState({
                hasStream: !!audioElementRef.current?.srcObject,
                isPlaying: !audioElementRef.current?.paused,
                currentTime: audioElementRef.current?.currentTime || 0,
                readyState: audioElementRef.current?.readyState || 0,
              });
            } catch (error) {
              console.error("Audio binding failed:", error);
              setNeedsAudioUnmute(true);
              reportProblem("audio_bind_failed", {
                error: describeError(error),
                playback: describeAudioPlayback(
                  audioElementRef.current,
                  audioContextRef.current
                ),
              });
              showToastOnce(
                "speaker-not-connected",
                "Speaker audio not connected - tap 'Enable Audio' to try again",
                "error"
              );
            }
          },
        };

        addAudioVideoObserver(audioLevelObserver);

        addAudioVideoObserver({
          audioVideoDidStop: (sessionStatus) => {
            const statusCode = sessionStatus?.statusCode?.();
            const details = {
              statusCode,
              status: MeetingSessionStatusCode[statusCode],
            };
            callStatsRef.current?.noteStop(details.status);
            const isNormalStop =
              statusCode === MeetingSessionStatusCode.OK ||
              statusCode === MeetingSessionStatusCode.Left ||
              statusCode === MeetingSessionStatusCode.MeetingEnded;

            if (isNormalStop) {
              reportDiagnostic("meeting_stopped", details);
            } else {
              reportProblem("meeting_stopped", details);
            }
          },
        });

        eventObserverRef.current = {
          eventDidReceive: (name, attributes) => {
            const details = {
              attributes: summariseChimeEventAttributes(attributes),
            };

            if (CHIME_PROBLEM_EVENTS.has(name)) {
              reportProblem(`chime_${name}`, details);
            } else if (CHIME_MILESTONE_EVENTS.has(name)) {
              reportDiagnostic(`chime_${name}`, details);
            }
          },
        };
        meetingSession.eventController?.addObserver(eventObserverRef.current);

        meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
          meetingSession.configuration.credentials.attendeeId,
          (attendeeId, volume, muted, _signalStrength) => {
            // Chime fires this callback for any of {volume, muted,
            // signalStrength} changes; unchanged fields arrive as null.
            // Only react to actual volume samples — and only flip back to
            // 0 when we know muted is true. Otherwise we'd zero the bar
            // every signal-strength tick.
            if (muted === true) {
              setMicrophoneLevel(0);
              return;
            }
            if (typeof volume === "number" && Number.isFinite(volume)) {
              const volumePercentage = Math.max(
                0,
                Math.min(100, Math.round(volume * 100))
              );
              setMicrophoneLevel(volumePercentage);
            }
          }
        );

        meetingSession.audioVideo.realtimeSubscribeToAttendeeIdPresence(
          (attendeeId, present) => {
            if (
              present &&
              attendeeId !== meetingSession.configuration.credentials.attendeeId
            ) {
              meetingSession.audioVideo.realtimeSubscribeToVolumeIndicator(
                attendeeId,
                (remoteAttendeeId, volume, muted) => {
                  setRemoteParticipantMuted(muted);

                  setRemoteSpeakerLevels((prev) => {
                    if (!muted && volume !== null && volume > 0) {
                      const volumePercentage = Math.round(volume * 100);
                      return { ...prev, [remoteAttendeeId]: volumePercentage };
                    } else {
                      const updated = { ...prev };
                      delete updated[remoteAttendeeId];
                      return updated;
                    }
                  });
                }
              );
            } else if (
              !present &&
              attendeeId !== meetingSession.configuration.credentials.attendeeId
            ) {
              handleRemoteAttendeeLeft();
              setRemoteParticipantMuted(false);
              setRemoteSpeakerLevels((prev) => {
                const updated = { ...prev };
                delete updated[attendeeId];
                return updated;
              });
            }
          }
        );

        // The microphone must be running before start(): Chime v3 otherwise
        // joins with no audio input and swaps the track in later, and the
        // permission check's open-then-stop of a mic stream must not happen
        // while remote audio is already playing (iOS re-routes or silences it).
        let isAudioReady = false;
        try {
          await checkMicrophonePermissions();

          await initializeAudioDevices(meetingSession);
          isAudioReady = true;
        } catch (audioError) {
          console.error("❌ Audio initialization failed:", audioError);
          reportProblem("audio_setup_failed", {
            error: describeError(audioError),
            microphonePermission: await getMicrophonePermissionState(),
          });
          showToastOnce(
            "audio-setup-failed",
            `Audio setup failed: ${audioError.message}`,
            "error"
          );
        }

        const videoInputDevices =
          await meetingSession.audioVideo.listVideoInputDevices();

        if (videoInputDevices.length > 0) {
          try {
            const savedVideoId = getSavedDevicePreference(
              STORAGE_KEYS.videoInput
            );
            const savedVideoDevice = findSavedDevice(
              videoInputDevices,
              savedVideoId
            );
            const videoDeviceToUse = pickCameraDevice(videoInputDevices, {
              savedDeviceId: savedVideoId,
              browserDefaultDeviceId: browserDefaultCameraId,
            });

            // Read the saved blur preference directly rather than the isBlurEnabled state: this
            // effect only runs once on mount (deps: [credentials, meetingId]), before the
            // state-loading effect's update has had a render to take effect. Doctor-only feature —
            // never applied on a participant (patient) join.
            const blurPreferredOnJoin =
              !isParticipantView &&
              getSavedDevicePreference(STORAGE_KEYS.videoBlur) === "1";
            const resolvedJoinDevice = await blurDeviceResolverRef.current.resolveDevice(
              videoDeviceToUse.deviceId,
              blurPreferredOnJoin
            );
            await meetingSession.audioVideo.startVideoInput(resolvedJoinDevice);
            setSelectedVideoInput(videoDeviceToUse);

            const cameraSource =
              videoDeviceToUse.deviceId === savedVideoId
                ? "saved"
                : videoDeviceToUse.deviceId === browserDefaultCameraId
                  ? "browser_default"
                  : "device_list";
            reportDiagnostic("camera_selected", {
              camera: videoDeviceToUse.label,
              source: cameraSource,
              looksUnsuitable: isUnsuitableCamera(videoDeviceToUse),
              cameraCount: videoInputDevices.length,
            });

            if (savedVideoDevice) {
            } else {
            }
          } catch (error) {
            console.warn("Failed to initialize video device:", error);
            toast.error(
              "Camera initialization failed, but you can still join with audio only",
              { duration: 1000 }
            );
          }
        } else {
          console.warn("No video input devices found");
          toast.error("No camera found, joining with audio only", {
            duration: 1000,
          });
        }

        meetingSession.audioVideo.start();

        meetingSession.audioVideo.startLocalVideoTile();

        // Retry local video if the camera picture doesn't appear on first attempt
        const retryLocalVideo = async (retries = 3, delayMs = 1500) => {
          for (let i = 0; i < retries; i++) {
            await new Promise((resolve) => setTimeout(resolve, delayMs));

            // Check if the local video element has a valid stream
            if (
              videoElementRef.current &&
              videoElementRef.current.srcObject &&
              videoElementRef.current.videoWidth > 0
            ) {
              return; // Video is working
            }

            console.warn(`Local video not loaded, retry attempt ${i + 1}/${retries}`);

            try {
              // Stop and restart video input
              meetingSession.audioVideo.stopLocalVideoTile();
              await meetingSession.audioVideo.stopVideoInput();

              const videoDevices = await meetingSession.audioVideo.listVideoInputDevices();
              if (videoDevices.length > 0) {
                const deviceToUse = pickCameraDevice(videoDevices, {
                  savedDeviceId: getSavedDevicePreference(STORAGE_KEYS.videoInput),
                  browserDefaultDeviceId: browserDefaultCameraId,
                });
                const resolvedRetryDevice = await blurDeviceResolverRef.current.resolveDevice(
                  deviceToUse.deviceId,
                  !isParticipantView &&
                    getSavedDevicePreference(STORAGE_KEYS.videoBlur) === "1"
                );
                await meetingSession.audioVideo.startVideoInput(resolvedRetryDevice);
                meetingSession.audioVideo.startLocalVideoTile();
              }
            } catch (retryError) {
              console.warn(`Video retry ${i + 1} failed:`, retryError);
            }
          }
        };

        if (isAudioReady) {
          try {
            await loadAvailableDevices(meetingSession);

            verifyAudioTransmission(meetingSession);
          } catch (audioError) {
            console.error("❌ Audio monitoring setup failed:", audioError);
          }
        }

        setIsConnected(true);
        setIsLoading(false);

        requestWakeLock();

        // Retry video in the background after initial load
        retryLocalVideo();
      } catch (err) {
        console.error("Error initializing Chime meeting:", err);
        setError(err.message || "Failed to join meeting");
        showToastOnce("failed-to-join", "Failed to join meeting", "error");
        setIsLoading(false);
      }
    };

    // React StrictMode mounts components twice in development. Without a
    // guard, each mount opens a Chime session with the same attendeeId
    // and the server kicks the first one with AudioJoinedFromAnotherDevice.
    // We defer init by one macrotask so that StrictMode's cleanup pass can
    // run first and cancel the pending init before any Chime session is
    // created.
    let cancelled = false;
    const initDelayTimer = setTimeout(() => {
      if (cancelled) return;
      initializeChimeMeeting();
    }, 0);

    const handleBeforeUnload = () => {
      cleanupMeeting();
    };

    const handleDeviceChange = async () => {
      if (meetingSessionRef.current && isConnectedRef.current) {
        setTimeout(async () => {
          try {
            await loadAvailableDevices(meetingSessionRef.current);
          } catch (e) {}

          // iOS Safari keeps the audio element pinned to the old sink when
          // AirPods / BT devices connect or disconnect. Rebinding under the
          // existing user-gesture-warmed audio session is the only reliable
          // way to make Safari pick up the new route.
          if (isIOS) {
            try {
              await applyIOSAudioRouteWorkaround();
            } catch (e) {
              console.warn(
                "iOS rebind on devicechange failed:",
                e
              );
            }
          }
        }, 1000);
      }
    };

    // iOS Safari (and Android Chrome on Bluetooth handoff) suspends WebRTC
    // audio when the tab loses focus. Re-binding the audio element when we
    // come back to foreground recovers playback without forcing the user to
    // hit "Tap to Enable Audio" again.
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "hidden") {
        sendCallSummary({ isFinal: false });

        return;
      }
      if (document.visibilityState !== "visible") return;

      requestWakeLock();

      const audioElement = audioElementRef.current;
      const meetingSession = meetingSessionRef.current;
      if (!audioElement || !meetingSession) return;

      try {
        if (audioContextRef.current?.state === "suspended") {
          await audioContextRef.current.resume();
        }
        await meetingSession.audioVideo.bindAudioElement(audioElement);
        audioElement.muted = false;

        if (audioElement.paused) {
          try {
            await audioElement.play();
            setNeedsAudioUnmute(false);
          } catch (playError) {
            setNeedsAudioUnmute(true);
            reportPlaybackBlocked("visibility_change", playError);
          }
        }
      } catch (e) {
        console.warn("Audio recovery on visibility change failed:", e);
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    document.addEventListener("visibilitychange", handleVisibilityChange);
    if (navigator.mediaDevices) {
      navigator.mediaDevices.addEventListener(
        "devicechange",
        handleDeviceChange
      );
    }

    return () => {
      cancelled = true;
      clearTimeout(initDelayTimer);
      window.removeEventListener("beforeunload", handleBeforeUnload);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      if (navigator.mediaDevices) {
        navigator.mediaDevices.removeEventListener(
          "devicechange",
          handleDeviceChange
        );
      }
      cleanupMeeting();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentials, meetingId]);

  const handleToggleMute = () => {
    if (!meetingSessionRef.current) return;

    if (audioContextRef.current?.state === "suspended") {
      audioContextRef.current.resume().catch(() => {});
    }

    if (isMuted) {
      meetingSessionRef.current.audioVideo.realtimeUnmuteLocalAudio();
      setIsMuted(false);
      setMicrophoneStatus("active");
    } else {
      meetingSessionRef.current.audioVideo.realtimeMuteLocalAudio();
      setIsMuted(true);
      setMicrophoneLevel(0);
      setMicrophoneStatus("muted");
    }
  };

  const enableAudioPlayback = async () => {
    const audioElement = audioElementRef.current;
    const meetingSession = meetingSessionRef.current;

    if (!audioElement || !meetingSession) {
      console.warn("Audio element or meeting session not available");
      return;
    }

    const MAX_ATTEMPTS = 3;
    let lastEnableError = null;

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      try {
        // Re-bind on every attempt — if the srcObject was lost (network
        // reconnection, iOS background‑suspend) this refreshes it.
        await meetingSession.audioVideo.bindAudioElement(audioElement);

        audioElement.muted = false;
        audioElement.volume = 1.0;

        // iOS Safari starts AudioContext in `suspended`; resume on this
        // confirmed user gesture so the analyser/visualizer paths run.
        if (audioContextRef.current?.state === "suspended") {
          try {
            await audioContextRef.current.resume();
          } catch (_resumeError) {}
        }

        // On the first attempt, prime the AudioContext with a tiny silent
        // buffer — iOS Safari requires a user‑gesture‑driven play() on
        // *any* audio before it will unmute the hardware output.
        if (attempt === 1) {
          try {
            const silentAudio = new Audio(
              "data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleDs/"
            );
            silentAudio.volume = 0.01;
            await silentAudio.play();
            silentAudio.pause();
          } catch (_silentError) {
            // Non‑fatal — continue with the real play()
          }
        }

        await audioElement.play();

        setNeedsAudioUnmute(false);
        setAudioEnabledByUser(true);
        reportDiagnostic("audio_enabled_by_user", {
          attempt,
          playback: describeAudioPlayback(audioElement, audioContextRef.current),
        });

        setAudioElementState({
          hasStream: !!audioElement.srcObject,
          isPlaying: !audioElement.paused,
          currentTime: audioElement.currentTime,
          readyState: audioElement.readyState,
        });

        return;
      } catch (error) {
        console.warn(`Audio enable attempt ${attempt}/${MAX_ATTEMPTS} failed:`, error);
        lastEnableError = error;

        if (attempt < MAX_ATTEMPTS) {
          await new Promise((resolve) => setTimeout(resolve, 300 * attempt));
        }
      }
    }

    reportProblem("audio_enable_failed", {
      error: describeError(lastEnableError),
      playback: describeAudioPlayback(audioElement, audioContextRef.current),
    });
    toast.error(
      "Couldn't enable audio. Check your device volume isn't muted, then tap the audio button again.",
      { id: "audio-enable-failed", duration: 5000 }
    );
  };

  /**
   * Pick the camera deviceId we should use when starting/restarting video.
   * Prefers a previously saved preference, falls back to current selection,
   * then to the first available input.
   */
  const pickVideoDeviceId = () => {
    const savedVideoId = getSavedDevicePreference(STORAGE_KEYS.videoInput);
    let deviceToUse = null;

    if (savedVideoId && availableVideoInputs.length > 0) {
      deviceToUse = availableVideoInputs.find(
        (d) => d.deviceId === savedVideoId
      );
    }
    if (!deviceToUse && availableVideoInputs.length > 0) {
      deviceToUse =
        selectedVideoInput ||
        pickCameraDevice(availableVideoInputs, {
          browserDefaultDeviceId: browserDefaultCameraId,
        });
    }

    return deviceToUse;
  };

  /**
   * Tear down the local camera input cleanly. Stops the tile, releases the
   * input, and unbinds the <video> element so the OS releases the camera
   * before any subsequent restart.
   */
  const teardownLocalVideo = async () => {
    if (!meetingSessionRef.current) return;

    try {
      meetingSessionRef.current.audioVideo.stopLocalVideoTile();
    } catch (e) {
      console.warn("stopLocalVideoTile failed:", e);
    }

    try {
      await meetingSessionRef.current.audioVideo.stopVideoInput();
    } catch (e) {
      console.warn("stopVideoInput failed:", e);
    }

    if (videoElementRef.current) {
      videoElementRef.current.srcObject = null;
    }
    localVideoTileIdRef.current = null;

    if (videoHealthWatchdogRef.current) {
      clearTimeout(videoHealthWatchdogRef.current);
      videoHealthWatchdogRef.current = null;
    }
  };

  /**
   * Start the local camera. Picks the device, starts the input, then starts
   * the local tile. Caller is responsible for tearing down first.
   */
  const startLocalVideo = async () => {
    if (!meetingSessionRef.current) return;

    const deviceToUse = pickVideoDeviceId();

    if (deviceToUse) {
      const resolvedDevice = await blurDeviceResolverRef.current.resolveDevice(
        deviceToUse.deviceId,
        isBlurEnabled
      );
      await meetingSessionRef.current.audioVideo.startVideoInput(
        resolvedDevice
      );
      setSelectedVideoInput(deviceToUse);
    } else {
      await meetingSessionRef.current.audioVideo.startVideoInput(null);
    }

    meetingSessionRef.current.audioVideo.startLocalVideoTile();
  };

  /**
   * After a successful local tile bind, verify the video stream is actually
   * producing live frames. If not, attempt one automatic recovery — this is
   * what fixes "I turned my video back on but I/the other person see black".
   */
  const scheduleLocalVideoHealthCheck = () => {
    if (videoHealthWatchdogRef.current) {
      clearTimeout(videoHealthWatchdogRef.current);
    }

    videoHealthWatchdogRef.current = setTimeout(() => {
      const videoElement = videoElementRef.current;
      if (!videoElement) return;

      const stream = videoElement.srcObject;
      const videoTrack = stream?.getVideoTracks?.()[0];
      const isLive =
        videoTrack &&
        videoTrack.readyState === "live" &&
        !videoTrack.muted;

      if (!isLive) {
        console.warn(
          "Local video health check failed — attempting recovery",
          {
            hasStream: !!stream,
            trackReadyState: videoTrack?.readyState,
            trackMuted: videoTrack?.muted,
          }
        );
        recoverLocalVideo("health_check_failed");
      } else {
        videoRecoveryAttemptsRef.current = 0;
      }
    }, 3000);
  };

  /**
   * Attempt to recover from a black/dead local video stream.
   * Tears down, waits for OS to release the camera, then restarts.
   * Caps at 2 attempts to avoid infinite loops.
   */
  const recoverLocalVideo = async (reason) => {
    if (!meetingSessionRef.current) return;

    // Check the SDK's source of truth for "user has video on", not React
    // state — the observer closure captures stale state from mount time.
    const hasLocalVideo =
      meetingSessionRef.current.audioVideo.hasStartedLocalVideoTile?.() ??
      !!localVideoTileIdRef.current;

    if (!hasLocalVideo) return;

    if (videoRecoveryAttemptsRef.current >= 2) {
      console.warn("Local video recovery — max attempts reached, giving up");
      toast.error(
        "Camera isn't sending video. Toggle the video button off, then on, to reset it.",
        { id: "camera-not-working", duration: 5000 }
      );
      return;
    }

    videoRecoveryAttemptsRef.current += 1;
    console.log(
      `Recovering local video (reason: ${reason}, attempt ${videoRecoveryAttemptsRef.current})`
    );

    try {
      await teardownLocalVideo();
      // Wait for the OS to release the camera before re-acquiring.
      await new Promise((resolve) => setTimeout(resolve, 400));
      await startLocalVideo();
    } catch (e) {
      console.error("Local video recovery failed:", e);
      toast.error(
        "Couldn't recover the camera automatically. Toggle the video button off, then on.",
        { id: "camera-recovery-failed", duration: 5000 }
      );
    }
  };

  const handleToggleVideo = async () => {
    if (!meetingSessionRef.current) return;

    try {
      if (isVideoOff) {
        videoRecoveryAttemptsRef.current = 0;
        await startLocalVideo();
        setIsVideoOff(false);
      } else {
        await teardownLocalVideo();
        setIsVideoOff(true);
      }
    } catch (error) {
      console.error("❌ Failed to toggle video:", error);
      toast.error("Failed to toggle camera", { duration: 1000 });

      try {
        if (!isVideoOff) {
          await teardownLocalVideo();
          setIsVideoOff(true);
        }
      } catch (e) {
        console.error("Recovery failed:", e);
      }
    }
  };

  const handleLeaveMeeting = async () => {
    await cleanupMeeting();

    if (onLeave) {
      onLeave();
    }
  };

  // The <audio> element MUST be in the DOM before Chime fires
  // audioVideoDidStart, otherwise audioElementRef.current is null and
  // bindAudioElement throws "Audio element is required". Rendering it
  // here, ahead of every early return, guarantees the ref is set on the
  // first render — well before initializeChimeMeeting starts the session.
  const hiddenAudioElement = (
    /* eslint-disable-next-line jsx-a11y/media-has-caption */
    <audio
      ref={audioElementRef}
      autoPlay
      playsInline
      style={{ display: "none" }}
    />
  );

  if (isLoading) {
    return (
      <div className={styles.container}>
        {hiddenAudioElement}
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Connecting to video call...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.container}>
        {hiddenAudioElement}
        <div className={styles.error}>
          <h3>Unable to join meeting</h3>
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {hiddenAudioElement}

      <div className={styles.videoGrid}>
        <div className={styles.videoMain}>
          <video
            ref={remoteVideoElementRef}
            className={styles.remoteVideo}
            autoPlay
            playsInline
            // Always contain so the whole remote frame is shown, never cropped
            // or zoomed. The tiles are wider than the incoming stream (and iOS
            // sends a landscape stream even when the phone is held upright), so
            // cover would zoom into a slice ("top of head"). Letterboxes against
            // the dark background.
            style={{ objectFit: "contain", backgroundColor: "#000" }}
          />
          {!isConnected && !showReconnectingOverlay && (
            <div className={styles.videoPlaceholder}>
              <p>Waiting for other participants...</p>
            </div>
          )}

          {/* Reconnection overlay */}
          {showReconnectingOverlay && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(0, 0, 0, 0.8)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 50,
              }}
            >
              <div
                style={{
                  width: "40px",
                  height: "40px",
                  border: "3px solid rgba(255,255,255,0.3)",
                  borderTopColor: "#fff",
                  borderRadius: "50%",
                  animation: "spin 1s linear infinite",
                }}
              />
              <p
                style={{
                  color: "#fff",
                  marginTop: "16px",
                  fontSize: "14px",
                  fontWeight: 500,
                }}
              >
                Restoring the call…
              </p>
              <p
                style={{
                  color: "rgba(255,255,255,0.75)",
                  marginTop: "8px",
                  fontSize: "12px",
                  textAlign: "center",
                  maxWidth: "320px",
                  lineHeight: 1.4,
                }}
              >
                Audio dropped out — usually a brief Wi-Fi or network blip.
                Attempt {reconnectionAttempts} of{" "}
                {VIDEO_CALL_CONFIG.MAX_RECONNECTION_ATTEMPTS}.
              </p>
              <button
                onClick={triggerReconnection}
                style={{
                  marginTop: "16px",
                  padding: "8px 16px",
                  backgroundColor: "rgba(255,255,255,0.2)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  borderRadius: "6px",
                  color: "#fff",
                  cursor: "pointer",
                  fontSize: "12px",
                }}
              >
                Retry Now
              </button>
            </div>
          )}

          {/* Poor connection warning banner */}
          {isConnected &&
            (networkQuality === NetworkQuality.POOR ||
              networkQuality === NetworkQuality.CRITICAL) && (
              <div
                style={{
                  position: "absolute",
                  bottom: "8px",
                  right: "8px",
                  left: "8px",
                  display: "flex",
                  alignItems: "flex-start",
                  gap: "8px",
                  background: "rgba(239, 68, 68, 0.92)",
                  padding: "10px 14px",
                  borderRadius: "10px",
                  zIndex: 15,
                  maxWidth: "520px",
                  marginLeft: "auto",
                }}
                title={getConnectionQualityDescription(networkQuality)}
              >
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  style={{ flexShrink: 0, marginTop: "2px" }}
                >
                  <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                  <line x1="12" y1="9" x2="12" y2="13"></line>
                  <line x1="12" y1="17" x2="12.01" y2="17"></line>
                </svg>
                <span
                  style={{
                    color: "#ffffff",
                    fontSize: "12px",
                    fontWeight: 500,
                    lineHeight: 1.4,
                  }}
                >
                  {getConnectionQualityDescription(networkQuality)}
                </span>
              </div>
            )}

          {isConnected && remoteParticipantMuted && (
            <div
              style={{
                position: "absolute",
                top: "8px",
                left: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(239, 68, 68, 0.9)",
                padding: "6px 12px",
                borderRadius: "16px",
                zIndex: 15,
              }}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#ffffff"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="1" y1="1" x2="23" y2="23"></line>
                <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
                <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
                <line x1="12" y1="19" x2="12" y2="23"></line>
                <line x1="8" y1="23" x2="16" y2="23"></line>
              </svg>
              <span
                style={{
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: "600",
                }}
              >
                {remoteLabel} Muted
              </span>
            </div>
          )}

          {isConnected &&
            (!audioEnabledByUser ||
              needsAudioUnmute ||
              (audioElementState.hasStream &&
                !audioElementState.isPlaying)) && (
              <div
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  zIndex: 100,
                  width: isParticipantView
                    ? isNarrowViewport
                      ? "calc(100% - 24px)"
                      : "92%"
                    : "auto",
                  maxWidth: isParticipantView
                    ? isNarrowViewport
                      ? "360px"
                      : "440px"
                    : "420px",
                  maxHeight: isNarrowViewport ? "calc(100% - 16px)" : "none",
                  overflowY: isNarrowViewport ? "auto" : "visible",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: isParticipantView
                    ? isNarrowViewport
                      ? "10px"
                      : "16px"
                    : "12px",
                  background:
                    "linear-gradient(135deg, #006546 0%, #059669 100%)",
                  borderRadius: isParticipantView
                    ? isNarrowViewport
                      ? "16px"
                      : "24px"
                    : "16px",
                  padding: isParticipantView
                    ? isNarrowViewport
                      ? "18px 18px"
                      : "32px 32px"
                    : "24px 24px",
                  boxShadow: isParticipantView
                    ? "0 12px 48px rgba(0, 101, 70, 0.5), 0 4px 16px rgba(0, 0, 0, 0.3)"
                    : "0 8px 32px rgba(0, 0, 0, 0.3)",
                  boxSizing: "border-box",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: isNarrowViewport ? "6px" : "8px",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={
                      isParticipantView
                        ? isNarrowViewport
                          ? "40"
                          : "64"
                        : "44"
                    }
                    height={
                      isParticipantView
                        ? isNarrowViewport
                          ? "40"
                          : "64"
                        : "44"
                    }
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                  </svg>
                  <span
                    style={{
                      color: "white",
                      fontSize: isParticipantView
                        ? isNarrowViewport
                          ? "17px"
                          : "22px"
                        : "18px",
                      fontWeight: "700",
                      textAlign: "center",
                    }}
                  >
                    {isParticipantView
                      ? `Tap Here to Hear the ${remoteLabel}`
                      : "Enable Audio"}
                  </span>
                  {!(isParticipantView && isNarrowViewport) && (
                    <span
                      style={{
                        color: "rgba(255, 255, 255, 0.9)",
                        fontSize: isParticipantView ? "14px" : "12px",
                        textAlign: "center",
                        lineHeight: "1.4",
                      }}
                    >
                      Pick your microphone and speaker, then tap to start audio.
                    </span>
                  )}
                </div>

                {/*
                  On phones (participant view) the device selectors push
                  the panel past the viewport and most users won't switch
                  away from the default mic/speaker anyway — they can use
                  the settings cog after enabling audio if they need to.
                */}
                {!(isParticipantView && isNarrowViewport) && (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                  }}
                >
                  {availableAudioInputs.length > 0 && (
                    <label
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <span
                        style={{
                          color: "rgba(255,255,255,0.95)",
                          fontSize: "12px",
                          fontWeight: 600,
                        }}
                      >
                        {localLabel}: Microphone
                      </span>
                      <select
                        value={selectedAudioInput?.deviceId || ""}
                        onChange={(e) => {
                          const device = availableAudioInputs.find(
                            (d) => d.deviceId === e.target.value
                          );
                          if (device) changeAudioInputDevice(device);
                        }}
                        style={{
                          width: "100%",
                          padding: "8px 10px",
                          borderRadius: "6px",
                          border: "1px solid rgba(255,255,255,0.3)",
                          backgroundColor: "rgba(0,0,0,0.25)",
                          color: "#ffffff",
                          fontSize: "13px",
                        }}
                      >
                        {availableAudioInputs.map((device) => (
                          <option
                            key={device.deviceId}
                            value={device.deviceId}
                            style={{ color: "#000" }}
                          >
                            {device.label ||
                              `Microphone ${device.deviceId.slice(0, 8)}...`}
                          </option>
                        ))}
                      </select>
                    </label>
                  )}

                  {(() => {
                    const speakerOptions = isIOS
                      ? IOS_SPEAKER_OPTIONS
                      : availableAudioOutputs;
                    if (speakerOptions.length === 0) return null;

                    return (
                      <label
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "4px",
                        }}
                      >
                        <span
                          style={{
                            color: "rgba(255,255,255,0.95)",
                            fontSize: "12px",
                            fontWeight: 600,
                          }}
                        >
                          {localLabel}: Speaker
                        </span>
                        <select
                          value={selectedAudioOutput?.deviceId || ""}
                          onChange={(e) => {
                            const device = speakerOptions.find(
                              (d) => d.deviceId === e.target.value
                            );
                            if (device) changeAudioOutputDevice(device);
                          }}
                          style={{
                            width: "100%",
                            padding: "8px 10px",
                            borderRadius: "6px",
                            border: "1px solid rgba(255,255,255,0.3)",
                            backgroundColor: "rgba(0,0,0,0.25)",
                            color: "#ffffff",
                            fontSize: "13px",
                          }}
                        >
                          {speakerOptions.map((device) => (
                            <option
                              key={device.deviceId}
                              value={device.deviceId}
                              style={{ color: "#000" }}
                            >
                              {device.label ||
                                `Speaker ${device.deviceId.slice(-4)}`}
                            </option>
                          ))}
                        </select>
                        {isIOS && (
                          <span
                            style={{
                              color: "rgba(255,255,255,0.85)",
                              fontSize: "11px",
                              lineHeight: 1.3,
                            }}
                          >
                            iOS limits speaker switching — use Control Center if
                            audio is too quiet.
                          </span>
                        )}
                      </label>
                    );
                  })()}
                </div>
                )}

                <button
                  onClick={enableAudioPlayback}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: "10px",
                    background: "rgba(255,255,255,0.15)",
                    border: "2px solid rgba(255,255,255,0.6)",
                    borderRadius: "12px",
                    padding: isParticipantView
                      ? isNarrowViewport
                        ? "12px 18px"
                        : "16px 24px"
                      : "12px 20px",
                    color: "#ffffff",
                    fontSize: isParticipantView
                      ? isNarrowViewport
                        ? "15px"
                        : "18px"
                      : "16px",
                    fontWeight: 700,
                    cursor: "pointer",
                    animation: "pulse 2s infinite",
                  }}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="22"
                    height="22"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                    <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
                    <path d="M19.07 4.93a10 10 0 0 1 0 14.14"></path>
                  </svg>
                  Tap to Enable Audio
                </button>
              </div>
            )}

          {/* Connection and audio status indicators */}
          <div
            style={{
              position: "absolute",
              top: "8px",
              right: "8px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
              background: "rgba(0, 0, 0, 0.7)",
              padding: "6px 10px",
              borderRadius: "16px",
              backdropFilter: "blur(10px)",
              fontSize: "10px",
              color: "#fff",
              zIndex: 10,
            }}
          >
            {/* Network quality indicator (local) */}
            <div
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
              title={`${localLabel}'s network quality`}
            >
              <div
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background:
                    networkQuality === NetworkQuality.EXCELLENT ||
                    networkQuality === NetworkQuality.GOOD
                      ? "#4ade80"
                      : networkQuality === NetworkQuality.FAIR
                      ? "#fbbf24"
                      : "#ef4444",
                }}
              />
              <span>
                {localLabel}:{" "}
                {networkQuality === NetworkQuality.CRITICAL
                  ? "Poor Net"
                  : networkQuality === NetworkQuality.POOR
                  ? "Weak Net"
                  : "Net"}
              </span>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
              title={`Audio you hear from the ${remoteLabel.toLowerCase()}`}
            >
              <div
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background:
                    audioElementState.isPlaying && audioHealthy
                      ? "#4ade80"
                      : "#fbbf24",
                }}
              />
              <span>
                {remoteLabel}:{" "}
                {audioElementState.isPlaying && audioHealthy
                  ? "Audio OK"
                  : "No Audio"}
              </span>
            </div>

            <div
              style={{ display: "flex", alignItems: "center", gap: "4px" }}
              title={`${localLabel}'s microphone`}
            >
              <div
                style={{
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background:
                    microphoneStatus === "active"
                      ? "#4ade80"
                      : microphoneStatus === "muted"
                      ? "#fbbf24"
                      : "#ef4444",
                }}
              />
              <span>
                {localLabel}:{" "}
                {microphoneStatus === "active"
                  ? "Mic"
                  : microphoneStatus === "muted"
                  ? "Muted"
                  : "Mic Off"}
              </span>
              {microphoneStatus === "active" && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-end",
                    gap: "1px",
                    height: "10px",
                  }}
                  aria-hidden="true"
                >
                  {[0, 1, 2].map((i) => {
                    const threshold = i * 30;
                    const active = microphoneLevel > threshold;
                    return (
                      <div
                        key={i}
                        style={{
                          width: "2px",
                          height: `${3 + i * 2}px`,
                          background: active
                            ? "#4ade80"
                            : "rgba(255,255,255,0.2)",
                          borderRadius: "1px",
                          transition: "background 120ms ease",
                        }}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          {(remoteAudioActive ||
            Object.keys(remoteSpeakerLevels).length > 0) && (
            <div
              style={{
                position: "absolute",
                bottom: "8px",
                left: "8px",
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: "rgba(0, 0, 0, 0.7)",
                padding: "4px 10px",
                borderRadius: "12px",
                backdropFilter: "blur(10px)",
              }}
              title={`${remoteLabel} is speaking`}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#4ade80"
                strokeWidth="2"
              >
                <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
                <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
              </svg>
              <span
                style={{
                  color: "#ffffff",
                  fontSize: "11px",
                  fontWeight: 600,
                }}
              >
                {remoteLabel}
              </span>
              <div
                style={{
                  display: "flex",
                  alignItems: "flex-end",
                  gap: "1px",
                  height: "12px",
                }}
              >
                {audioVisualizerData.slice(0, 8).map((value, i) => (
                  <div
                    key={i}
                    style={{
                      width: "2px",
                      height: `${Math.max(2, value * 0.12)}px`,
                      background:
                        value > 5 ? "#4ade80" : "rgba(255, 255, 255, 0.3)",
                      borderRadius: "1px",
                    }}
                  />
                ))}
              </div>
            </div>
          )}
        </div>

        <div
          className={styles.videoLocal}
          // Patient view: enlarge the self-preview slightly so the
          // patient can clearly see their own camera is working — a
          // common source of "is this working?" anxiety on telehealth
          // calls.
          style={
            isParticipantView
              ? { width: "180px", height: "135px" }
              : undefined
          }
        >
          <video
            ref={videoElementRef}
            className={styles.localVideo}
            autoPlay
            playsInline
            muted
          />
          {!isVideoOff && (practiceName || (isParticipantView && doctorName)) && (
            <div
              style={{
                position: "absolute",
                top: "6px",
                right: "6px",
                background: "rgba(0, 0, 0, 0.7)",
                color: "#ffffff",
                fontSize: "12px",
                fontWeight: "600",
                padding: "4px 8px",
                borderRadius: "4px",
                backdropFilter: "blur(10px)",
                maxWidth: "calc(100% - 12px)",
                textAlign: "right",
              }}
              title={[practiceName, isParticipantView && doctorName ? `Dr. ${doctorName}` : null]
                .filter(Boolean)
                .join(" — ")}
            >
              {practiceName && (
                <div
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {practiceName}
                </div>
              )}
              {isParticipantView && doctorName && (
                <div
                  style={{
                    fontSize: "11px",
                    fontWeight: "400",
                    color: "#d1d5db",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Dr. {doctorName}
                </div>
              )}
            </div>
          )}
          {isVideoOff && (
            <div className={styles.videoOffPlaceholder}>
              <p>Camera Off</p>
            </div>
          )}

          {/* Always-visible local mic barometer so the doctor can see at a
              glance whether their mic is picking up sound (any level, or
              none), and whether they're muted. */}
          <div
            style={{
              position: "absolute",
              bottom: "6px",
              left: "6px",
              right: "6px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              background: "rgba(0, 0, 0, 0.7)",
              padding: "4px 6px",
              borderRadius: "4px",
              backdropFilter: "blur(10px)",
            }}
            title={
              isMuted
                ? `${localLabel} is muted`
                : `${localLabel}'s microphone level`
            }
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke={isMuted ? "#ef4444" : "#4ade80"}
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z" />
              <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
              <line x1="12" y1="19" x2="12" y2="23" />
              <line x1="8" y1="23" x2="16" y2="23" />
              {isMuted && <line x1="2" y1="2" x2="22" y2="22" />}
            </svg>
            <span
              style={{
                color: "#ffffff",
                fontSize: "10px",
                fontWeight: 600,
              }}
            >
              {localLabel}
            </span>
            <div
              style={{
                flex: 1,
                height: "3px",
                background: "rgba(255, 255, 255, 0.2)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: isMuted ? "0%" : `${microphoneLevel}%`,
                  height: "100%",
                  background: isMuted
                    ? "transparent"
                    : microphoneLevel > 70
                    ? "#ef4444"
                    : microphoneLevel > 40
                    ? "#fbbf24"
                    : "#4ade80",
                  borderRadius: "2px",
                  transition: "width 0.1s ease",
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className={styles.videoControls}>
        <button
          onClick={handleToggleMute}
          className={`${styles.controlBtn} ${isMuted ? styles.active : ""}`}
          title={isMuted ? "Unmute" : "Mute"}
          style={{ position: "relative" }}
        >
          {!isMuted && microphoneLevel > 10 && (
            <div
              style={{
                position: "absolute",
                top: "-2px",
                right: "-2px",
                width: "10px",
                height: "10px",
                borderRadius: "50%",
                backgroundColor: "#4ade80",
                border: "2px solid white",
                animation: microphoneLevel > 30 ? "pulse 1s infinite" : "none",
              }}
            />
          )}
          {isMuted ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M9 9v3a3 3 0 0 0 5.12 2.12M15 9.34V4a3 3 0 0 0-5.94-.6"></path>
              <path d="M17 16.95A7 7 0 0 1 5 12v-2m14 0v2a7 7 0 0 1-.11 1.23"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="23"></line>
              <line x1="8" y1="23" x2="16" y2="23"></line>
            </svg>
          )}
        </button>

        <button
          onClick={handleToggleVideo}
          className={`${styles.controlBtn} ${isVideoOff ? styles.active : ""}`}
          title={isVideoOff ? "Turn on camera" : "Turn off camera"}
        >
          {isVideoOff ? (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <line x1="1" y1="1" x2="23" y2="23"></line>
              <path d="M10.66 5H14l3 3v8"></path>
              <path d="M16 16a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h2l10 10z"></path>
              <path d="M23 7l-7 5 7 5V7z"></path>
            </svg>
          ) : (
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M23 7l-7 5 7 5V7z"></path>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
          )}
        </button>

        {/* Background blur is a doctor-only feature, never offered on a patient's call. */}
        {!isParticipantView && (
          <button
            onClick={toggleBackgroundBlur}
            className={`${styles.controlBtn} ${isBlurEnabled ? styles.blurActive : ""}`}
            title={
              !isBlurSupported
                ? "Background blur not supported in this browser"
                : isBlurEnabled
                ? "Turn off background blur"
                : "Blur my background"
            }
            disabled={!isBlurSupported}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" strokeDasharray="2 2"></rect>
              <circle cx="12" cy="10" r="3"></circle>
              <path d="M7 19c0-2.8 2.2-5 5-5s5 2.2 5 5"></path>
            </svg>
          </button>
        )}

        {!isParticipantView && !isMobileDevice() && (
          <button
            onClick={onPopOut}
            className={styles.controlBtn}
            title="Open in new tab"
            aria-label="Open video in new tab"
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M21 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6"></path>
              <path d="M21 3h-6"></path>
              <path d="M10 14L21 3"></path>
            </svg>
          </button>
        )}

        <button
          onClick={() => setShowDeviceSettings(true)}
          className={styles.controlBtn}
          title="Device settings"
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </button>

        <button
          onClick={handleLeaveMeeting}
          className={styles.leaveBtn}
          title="Leave meeting"
          style={{
            display: "flex",
            alignItems: "center",
            gap: "6px",
            padding: "8px 16px",
            borderRadius: "20px",
            border: "none",
            background: "#ef4444",
            color: "#ffffff",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
          }}
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M18.36 6.64a9 9 0 1 1-12.73 0"></path>
            <line x1="12" y1="2" x2="12" y2="12"></line>
          </svg>
          End
        </button>
      </div>

      <div className={styles.meetingInfo}>
        <span className={styles.meetingLeftInfo}>
          {practiceName && (
            <>
              <span className={styles.practiceNameLabel}>{practiceName}</span>
              <span className={styles.meetingInfoDivider}>•</span>
            </>
          )}
          <span className={styles.meetingId}>Meeting ID: {meetingId}</span>
        </span>
        {isConnected && (
          <span className={styles.connectedIndicator}>
            <span className={styles.dot}></span>
            Connected
          </span>
        )}
      </div>

      {isConnected && (
        <div
          onClick={() => setShowDeviceSettings(true)}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexWrap: "wrap",
            gap: "12px",
            padding: "10px 16px",
            marginTop: "8px",
            backgroundColor: "#1f2937",
            borderRadius: "8px",
            cursor: "pointer",
            border: "1px solid #374151",
            transition: "background-color 0.2s ease",
          }}
          onMouseEnter={(e) =>
            (e.currentTarget.style.backgroundColor = "#374151")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.backgroundColor = "#1f2937")
          }
          title="Click to change devices"
        >
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={
                microphoneStatus === "active"
                  ? "#4ade80"
                  : microphoneStatus === "error"
                  ? "#ef4444"
                  : "#9ca3af"
              }
              strokeWidth="2"
            >
              <path d="M12 1a3 3 0 0 0-3 3v8a3 3 0 0 0 6 0V4a3 3 0 0 0-3-3z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
            </svg>
            <span
              style={{
                fontSize: "12px",
                color: "#d1d5db",
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedAudioInput?.label?.replace(/\s*\([^)]*\)\s*$/, "") ||
                "Default Mic"}
            </span>
          </div>

          <span style={{ color: "#4b5563", fontSize: "12px" }}>|</span>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#4ade80"
              strokeWidth="2"
            >
              <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"></polygon>
              <path d="M15.54 8.46a5 5 0 0 1 0 7.07"></path>
            </svg>
            <span
              style={{
                fontSize: "12px",
                color: "#d1d5db",
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedAudioOutput?.label?.replace(
                /\s*\([^)]*\)\s*$/,
                ""
              ) ||
                (isIOS ? "Speakerphone" : "Default Speaker")}
            </span>
          </div>
          <span style={{ color: "#4b5563", fontSize: "12px" }}>|</span>

          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke={isVideoOff ? "#fbbf24" : "#4ade80"}
              strokeWidth="2"
            >
              <path d="M23 7l-7 5 7 5V7z"></path>
              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
            </svg>
            <span
              style={{
                fontSize: "12px",
                color: "#d1d5db",
                maxWidth: "120px",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {selectedVideoInput?.label?.replace(/\s*\([^)]*\)\s*$/, "") ||
                "Default Camera"}
            </span>
          </div>

          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#6b7280"
            strokeWidth="2"
            style={{ marginLeft: "4px" }}
          >
            <circle cx="12" cy="12" r="3"></circle>
            <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1 1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"></path>
          </svg>
        </div>
      )}

      {showDeviceSettings && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
          }}
        >
          <div
            style={{
              backgroundColor: "#1f2937",
              borderRadius: "12px",
              padding: "24px",
              width: "90%",
              maxWidth: "500px",
              color: "#ffffff",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginBottom: "20px",
              }}
            >
              <h3 style={{ margin: 0, fontSize: "18px", fontWeight: "600" }}>
                Device Settings
              </h3>
              <button
                onClick={() => setShowDeviceSettings(false)}
                style={{
                  background: "none",
                  border: "none",
                  color: "#ffffff",
                  fontSize: "24px",
                  cursor: "pointer",
                  padding: "4px",
                }}
              >
                ×
              </button>
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Microphone
              </label>
              {availableAudioInputs.length > 0 ? (
                <select
                  value={selectedAudioInput?.deviceId || ""}
                  onChange={(e) => {
                    const device = availableAudioInputs.find(
                      (d) => d.deviceId === e.target.value
                    );
                    if (device) changeAudioInputDevice(device);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #374151",
                    backgroundColor: "#111827",
                    color: "#ffffff",
                    fontSize: "14px",
                  }}
                >
                  {availableAudioInputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label ||
                        `Microphone ${device.deviceId.slice(0, 8)}...`}
                    </option>
                  ))}
                </select>
              ) : (
                <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
                  Default microphone in use
                </p>
              )}
            </div>

            <div style={{ marginBottom: "20px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                }}
              >
                Camera
              </label>
              {availableVideoInputs.length > 0 ? (
                <select
                  value={selectedVideoInput?.deviceId || ""}
                  onChange={(e) => {
                    const device = availableVideoInputs.find(
                      (d) => d.deviceId === e.target.value
                    );
                    if (device) changeVideoInputDevice(device);
                  }}
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: "6px",
                    border: "1px solid #374151",
                    backgroundColor: "#111827",
                    color: "#ffffff",
                    fontSize: "14px",
                  }}
                >
                  {availableVideoInputs.map((device) => (
                    <option key={device.deviceId} value={device.deviceId}>
                      {device.label ||
                        `Camera ${device.deviceId.slice(0, 8)}...`}
                    </option>
                  ))}
                </select>
              ) : (
                <p style={{ margin: 0, fontSize: "13px", color: "#9ca3af" }}>
                  Default camera in use
                </p>
              )}
            </div>

            {(() => {
              const speakerOptions = isIOS
                ? IOS_SPEAKER_OPTIONS
                : availableAudioOutputs;
              if (speakerOptions.length === 0) return null;

              return (
                <div style={{ marginBottom: "20px" }}>
                  <label
                    style={{
                      display: "block",
                      marginBottom: "8px",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    Speaker
                  </label>
                  <select
                    value={selectedAudioOutput?.deviceId || ""}
                    onChange={(e) => {
                      const device = speakerOptions.find(
                        (d) => d.deviceId === e.target.value
                      );
                      if (device) changeAudioOutputDevice(device);
                    }}
                    style={{
                      width: "100%",
                      padding: "8px 12px",
                      borderRadius: "6px",
                      border: "1px solid #374151",
                      backgroundColor: "#111827",
                      color: "#ffffff",
                      fontSize: "14px",
                    }}
                  >
                    {speakerOptions.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Speaker ${device.deviceId.slice(-4)}`}
                      </option>
                    ))}
                  </select>
                </div>
              );
            })()}

            <div
              style={{
                marginBottom: "20px",
                padding: "12px",
                backgroundColor:
                  microphoneStatus === "active"
                    ? "rgba(34, 197, 94, 0.1)"
                    : microphoneStatus === "error"
                    ? "rgba(239, 68, 68, 0.1)"
                    : microphoneStatus === "muted"
                    ? "rgba(251, 191, 36, 0.1)"
                    : "rgba(107, 114, 128, 0.1)",
                borderRadius: "6px",
                border: `1px solid ${
                  microphoneStatus === "active"
                    ? "rgba(34, 197, 94, 0.3)"
                    : microphoneStatus === "error"
                    ? "rgba(239, 68, 68, 0.3)"
                    : microphoneStatus === "muted"
                    ? "rgba(251, 191, 36, 0.3)"
                    : "rgba(107, 114, 128, 0.3)"
                }`,
              }}
            >
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <div
                  style={{
                    width: "10px",
                    height: "10px",
                    borderRadius: "50%",
                    backgroundColor:
                      microphoneStatus === "active"
                        ? "#22c55e"
                        : microphoneStatus === "error"
                        ? "#ef4444"
                        : microphoneStatus === "muted"
                        ? "#fbbf24"
                        : "#6b7280",
                  }}
                />
                <span
                  style={{
                    fontSize: "13px",
                    color:
                      microphoneStatus === "active"
                        ? "#86efac"
                        : microphoneStatus === "error"
                        ? "#fca5a5"
                        : microphoneStatus === "muted"
                        ? "#fde68a"
                        : "#9ca3af",
                  }}
                >
                  Microphone:{" "}
                  {microphoneStatus === "active"
                    ? "Active"
                    : microphoneStatus === "error"
                    ? "Error - check device"
                    : microphoneStatus === "muted"
                    ? "Muted"
                    : "Initializing..."}
                </span>
                {microphoneLevel > 5 && microphoneStatus === "active" && (
                  <div
                    style={{
                      marginLeft: "auto",
                      display: "flex",
                      alignItems: "flex-end",
                      gap: "2px",
                      height: "16px",
                    }}
                  >
                    {[0, 1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        style={{
                          width: "3px",
                          height: `${4 + i * 3}px`,
                          background:
                            microphoneLevel > i * 20
                              ? "#22c55e"
                              : "rgba(255, 255, 255, 0.2)",
                          borderRadius: "1px",
                        }}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/iPhone|iPad|iPod/i.test(navigator.userAgent) && (
              <div
                style={{
                  marginBottom: "20px",
                  padding: "12px",
                  backgroundColor: "rgba(59, 130, 246, 0.1)",
                  borderRadius: "6px",
                  border: "1px solid rgba(59, 130, 246, 0.3)",
                }}
              >
                <p
                  style={{
                    margin: 0,
                    fontSize: "13px",
                    color: "#93c5fd",
                  }}
                >
                  Speaker selection is managed by your device. Use your iPhone's
                  volume controls or connect headphones/AirPods. If your
                  Bluetooth device isn't showing, tap "Refresh Devices" below.
                </p>
              </div>
            )}

            <div style={{ marginBottom: "16px" }}>
              <button
                onClick={refreshDeviceList}
                disabled={isRefreshingDevices}
                style={{
                  width: "100%",
                  backgroundColor: isRefreshingDevices ? "#374151" : "#1f2937",
                  color: "#ffffff",
                  border: "1px solid #4b5563",
                  borderRadius: "6px",
                  padding: "10px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: isRefreshingDevices ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                }}
              >
                {isRefreshingDevices ? (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      style={{ animation: "spin 1s linear infinite" }}
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Refreshing...
                  </>
                ) : (
                  <>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M21 2v6h-6"></path>
                      <path d="M3 12a9 9 0 0 1 15-6.7L21 8"></path>
                      <path d="M3 22v-6h6"></path>
                      <path d="M21 12a9 9 0 0 1-15 6.7L3 16"></path>
                    </svg>
                    Refresh Devices
                  </>
                )}
              </button>
              <p
                style={{
                  margin: "8px 0 0",
                  fontSize: "12px",
                  color: "#9ca3af",
                  textAlign: "center",
                }}
              >
                Tap to detect newly connected devices (AirPods, Bluetooth
                headsets)
              </p>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                onClick={() => setShowDeviceSettings(false)}
                style={{
                  backgroundColor: "#059669",
                  color: "#ffffff",
                  border: "none",
                  borderRadius: "6px",
                  padding: "8px 16px",
                  fontSize: "14px",
                  fontWeight: "500",
                  cursor: "pointer",
                }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export { InlineVideoCall };
